import { type Config } from 'tailwindcss';
import plugin from 'tailwindcss/plugin';
import tailwindColors from 'tailwindcss/colors';
import tailwindAnimateCss from 'tailwindcss-animate';
import tailwindTheme from 'tailwindcss/defaultTheme';

const basePreset: Config = {
  corePlugins: {
    preflight: true,
  },
  content: [
    '../ui-components/**/*.{js,jsx,ts,tsx}',
    '!../ui-components/**/node_modules/**/*.{js,jsx,ts,tsx}',
  ],
  theme: {
    screens: {
      xxs: '390px', // => @media (min-width: 390px) { ... }
      xs: '496px', // => @media (min-width: 496px) { ... }
      sm: '640px', // => @media (min-width: 640px) { ... }
      md: '768px', // => @media (min-width: 768px) { ... }
      lg: '1024px', // => @media (min-width: 1024px) { ... }
      xl: '1280px', // => @media (min-width: 1280px) { ... }
      '2xl': '1440px', // => @media (min-width: 1440px) { ... }
    },
    extend: {
      borderColor: {
        DEFAULT: tailwindColors.zinc[200],
      },
      borderRadius: {
        interface: tailwindTheme.borderRadius.md,
      },
      lineHeight: {
        extraTight: '1.15',
      },
      colors: {
        background: tailwindColors.white,
        brand: {
          DEFAULT: tailwindColors.pink[600],
          'aa-white': tailwindColors.pink[600],
          dark: tailwindColors.pink[800],
          foreground: tailwindColors.pink[600],
        },
        card: {
          DEFAULT: tailwindColors.white,
          foreground: tailwindColors.zinc[950],
        },
        foreground: {
          DEFAULT: tailwindColors.zinc[950],
          link: tailwindColors.blue[600],
          headline: tailwindColors.zinc[950],
          xdark: tailwindColors.zinc[950],
        },
        popover: {
          DEFAULT: tailwindColors.white,
          foreground: tailwindColors.zinc[950],
        },
        primary: {
          DEFAULT: tailwindColors.zinc[600],
          hover: tailwindColors.zinc[800],
          foreground: tailwindColors.white,
        },
        secondary: {
          DEFAULT: tailwindColors.zinc[100],
          hover: tailwindColors.zinc[200],
          foreground: {
            DEFAULT: tailwindColors.zinc[900],
            aa: tailwindColors.zinc[900],
          },
        },
        muted: {
          DEFAULT: tailwindColors.zinc[100],
          foreground: {
            DEFAULT: tailwindColors.zinc[600],
            link: tailwindColors.blue[600],
          },
        },
        accent: {
          DEFAULT: tailwindColors.zinc[100],
          foreground: tailwindColors.zinc[900],
        },
        info: {
          foreground: tailwindColors.blue[700],
          background: tailwindColors.blue[50],
          border: tailwindColors.blue[700],
        },
        success: {
          foreground: tailwindColors.green[700],
          background: tailwindColors.green[50],
          border: tailwindColors.green[700],
        },
        warning: {
          foreground: tailwindColors.amber[700],
          background: tailwindColors.amber[50],
          border: tailwindColors.amber[700],
        },
        error: {
          foreground: tailwindColors.red[700],
          background: tailwindColors.red[50],
          border: tailwindColors.red[700],
        },
        destructive: {
          DEFAULT: tailwindColors.red[600],
          foreground: tailwindColors.white,
          hover: tailwindColors.red[700],
        },
        border: tailwindColors.zinc[200],
        green: tailwindColors.green[500],
        input: {
          DEFAULT: tailwindColors.zinc[500],
          background: tailwindColors.white,
          foreground: {
            DEFAULT: tailwindColors.zinc[900],
            destructive: tailwindColors.red[600],
          },
          placeholder: tailwindColors.zinc[500],
        },
        ring: tailwindColors.blue[600],
        cta: {
          DEFAULT: tailwindColors.blue[600],
          foreground: tailwindColors.white,
          hover: tailwindColors.blue[700],
        },
        inverse: {
          DEFAULT: tailwindColors.zinc[800],
          foreground: tailwindColors.white,
        },
        chart: {
          DEFAULT: '#e76e50',
          1: '#e76e50',
          2: '#2a9d90',
          3: '#274754',
          4: '#e8c468',
          5: '#f4a462',
        },
      },
      boxShadow: {
        'top-lg':
          '0 -10px 15px -3px rgba(0, 0, 0, 0.1), 0 -4px 6px -4px rgba(0, 0, 0, 0.1)',
      },
      keyframes: {
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' },
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' },
        },
      },
      animation: {
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
      },
    },
  },

  plugins: [
    tailwindAnimateCss,
    plugin(function ({ addBase, theme }) {
      addBase({
        'ol,ul,menu': {
          listStyle: 'revert',
          margin: 'revert',
          padding: 'revert',
          paddingLeft: theme('size.4'),
        },
        a: {
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'none',
          },
        },
        blockquote: {
          margin: 'revert',
        },
        'img,svg': {
          display: 'revert',
        },
      });
    }),
  ],
};

export default basePreset;
